<template>
  <div class="timer-vs-display" :class="displayClass">
    <template v-if="isCustomTheme">
      <div class="vs-custom-layout">
        <div class="vs-custom-stage">
          <div class="vs-custom-mask" aria-hidden="true">
            <img class="vs-custom-mask__image" :src="customMaskSrc" alt="" />
          </div>

          <div class="vs-custom-overlay" :class="{ 'vs-custom-overlay--debug': layoutDebug }" :style="customStyleVars">
            <div v-if="layoutDebug" class="vs-custom-layout-debug-chip">
              {{ customLayoutProfile.key }} {{ customLayoutProfile.width }}x{{ customLayoutProfile.height }}
            </div>

            <div class="vs-custom-slot vs-custom-slot--athlete-name" :class="{ 'vs-custom-slot--debug': layoutDebug }" :style="getCustomSlotStyle('athleteName')" data-slot="athleteName">
              <span class="vs-custom-slot__label">athleteName</span>
              <span class="vs-custom-name" :title="activeAthleteDisplayName">
                <span class="vs-custom-name__text">{{ activeAthleteDisplayName }}</span>
                <span class="vs-custom-name__dot">.</span>
              </span>
            </div>

            <div v-if="showCustomCountryCode" class="vs-custom-slot vs-custom-slot--athlete-code" :class="{ 'vs-custom-slot--debug': layoutDebug }" :style="getCustomSlotStyle('athleteCode')" data-slot="athleteCode">
              <span class="vs-custom-slot__label">athleteCode</span>
              <span class="vs-custom-country">{{ activeCountryCode }}</span>
            </div>

            <div v-if="showCustomStatusControls" class="vs-custom-slot vs-custom-slot--status" :class="{ 'vs-custom-slot--debug': layoutDebug }" :style="getCustomSlotStyle('status')" data-slot="status">
              <span class="vs-custom-slot__label">status</span>
              <div class="custom-status-icon" :class="`custom-status-icon--${resolvedStatusIcon}`" :aria-label="activeStateLabel" :title="activeStateLabel">
                <template v-if="resolvedStatusIcon === 'pause'">
                  <span class="custom-status-icon__pause-bar"></span>
                  <span class="custom-status-icon__pause-bar"></span>
                </template>
                <span v-else-if="resolvedStatusIcon === 'play'" class="custom-status-icon__play"></span>
                <span v-else class="custom-status-icon__stop"></span>
              </div>
            </div>

            <div v-if="showCustomPowerIndicator" class="vs-custom-slot vs-custom-slot--power" :class="{ 'vs-custom-slot--debug': layoutDebug }" :style="getCustomSlotStyle('power')" data-slot="power">
              <span class="vs-custom-slot__label">power</span>
              <span class="custom-power-pill" :class="customPowerClass">{{ customPowerLabel }}</span>
            </div>

            <div class="vs-custom-slot vs-custom-slot--main-timer" :class="{ 'vs-custom-slot--debug': layoutDebug }" :style="getCustomSlotStyle('mainTimer')" data-slot="mainTimer">
              <span class="vs-custom-slot__label">mainTimer</span>
              <div class="vs-custom-timer" :class="{ 'vs-custom-timer--overtime': activeOvertime }">
                <span class="vs-custom-timer__main">{{ activeTimerDisplayParts.main }}</span>
                <span class="vs-custom-timer__fraction">{{ activeTimerDisplayParts.fraction }}</span>
              </div>
            </div>

            <div class="vs-custom-slot vs-custom-slot--secondary-timer" :class="{ 'vs-custom-slot--debug': layoutDebug }" :style="getCustomSlotStyle('secondaryTimer')" data-slot="secondaryTimer">
              <span class="vs-custom-slot__label">secondaryTimer</span>
              <div class="vs-custom-timer vs-custom-timer--secondary" :class="{ 'vs-custom-timer--overtime': inactiveOvertime }">
                <span class="vs-custom-timer__main">{{ inactiveTimerDisplayParts.main }}</span>
                <span class="vs-custom-timer__fraction">{{ inactiveTimerDisplayParts.fraction }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <template v-else>
      <div v-if="leftAthlete" class="athlete-bar athlete-bar--top">
        <div class="athlete-bar__info">
          <div class="flag" v-if="leftAthlete.flagUrl"><img class="flag-img" :src="leftAthlete.flagUrl" alt="flag" /></div>
          <div class="flag" v-else-if="leftAthlete.countryCode">
            <flag class="flag-svg" :iso="leftAthlete.countryCode.toLowerCase()" />
          </div>
          <span class="athlete-name" :title="leftAthleteDisplayName">{{ leftAthleteDisplayName }}</span>
        </div>
        <div class="status-pill" :class="leftStateClass">{{ leftStateLabel }}</div>
      </div>

      <div class="timer-layout timer-layout--vs">
        <div class="vs-body vs-body--top">
          <div class="vs-timer" :class="{ 'vs-timer--overtime': leftOvertime }">
            {{ leftDisplay }}
          </div>
        </div>

        <div class="vs-divider">
          <span class="vs-divider__line"></span>
          <span class="vs-divider__label">VS</span>
        </div>

        <div class="vs-body vs-body--bottom">
          <div class="vs-timer" :class="{ 'vs-timer--overtime': rightOvertime }">
            {{ rightDisplay }}
          </div>
        </div>
      </div>

      <div v-if="rightAthlete" class="athlete-bar athlete-bar--bottom">
        <div class="status-pill" :class="rightStateClass">{{ rightStateLabel }}</div>
        <div class="athlete-bar__info athlete-bar__info--reverse">
          <span class="athlete-name" :title="rightAthleteDisplayName">{{ rightAthleteDisplayName }}</span>
          <div class="flag" v-if="rightAthlete.flagUrl"><img class="flag-img" :src="rightAthlete.flagUrl" alt="flag" /></div>
          <div class="flag" v-else-if="rightAthlete.countryCode">
            <flag class="flag-svg" :iso="rightAthlete.countryCode.toLowerCase()" />
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import timerVsMask from '@/assets/timer_mask/timerVs.png';
import gShockVsMask from '@/assets/timer_mask/G-SHOCK_minimal_VS.png';
import {
  isCustomTimerTheme,
  normalizeTimerTheme,
} from '@/constants/timerThemes';
import {
  resolveCustomTimerLayoutProfile,
  resolveCustomTimerSlotStyles,
  resolveCustomTimerStyleVars,
} from '@/constants/timerLayoutPresets';

const CUSTOM_VS_MASKS = {
  custom: timerVsMask,
  custom_2: gShockVsMask,
};

export default {
  name: 'TimerVsDisplay',
  data() {
    return {
      viewportWidth: typeof window !== 'undefined' ? window.innerWidth || 1920 : 1920,
      viewportHeight: typeof window !== 'undefined' ? window.innerHeight || 1080 : 1080,
    };
  },
  props: {
    leftAthlete: {
      type: Object,
      default: null,
    },
    rightAthlete: {
      type: Object,
      default: null,
    },
    activeSide: {
      type: String,
      default: 'left',
    },
    leftDisplay: {
      type: String,
      default: '00:00',
    },
    leftRemainingSeconds: {
      type: Number,
      default: null,
    },
    leftStateValue: {
      type: String,
      default: 'idle',
    },
    leftIsRecording: {
      type: Boolean,
      default: false,
    },
    rightDisplay: {
      type: String,
      default: '00:00',
    },
    rightRemainingSeconds: {
      type: Number,
      default: null,
    },
    rightStateValue: {
      type: String,
      default: 'idle',
    },
    rightIsRecording: {
      type: Boolean,
      default: false,
    },
    leftOvertime: {
      type: Boolean,
      default: false,
    },
    rightOvertime: {
      type: Boolean,
      default: false,
    },
    leftStateLabel: {
      type: String,
      default: 'IDLE',
    },
    rightStateLabel: {
      type: String,
      default: 'IDLE',
    },
    leftStateClass: {
      type: [Object, Array, String],
      default: () => ({}),
    },
    rightStateClass: {
      type: [Object, Array, String],
      default: () => ({}),
    },
    theme: {
      type: String,
      default: 'dark',
    },
    layoutDebug: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    normalizedTheme() {
      return normalizeTimerTheme(this.theme);
    },
    displayClass() {
      return {
        'timer-vs-display--light': this.normalizedTheme === 'light',
        'timer-vs-display--custom': this.isCustomTheme,
        'timer-vs-display--custom-2': this.normalizedTheme === 'custom_2',
      };
    },
    isCustomTheme() {
      return isCustomTimerTheme(this.normalizedTheme);
    },
    isCustomThemeVariantTwo() {
      return this.normalizedTheme === 'custom_2';
    },
    customMaskSrc() {
      return CUSTOM_VS_MASKS[this.normalizedTheme] || CUSTOM_VS_MASKS.custom;
    },
    showCustomCountryCode() {
      return !this.isCustomThemeVariantTwo;
    },
    showCustomStatusControls() {
      return !this.isCustomThemeVariantTwo;
    },
    showCustomPowerIndicator() {
      return !this.isCustomThemeVariantTwo;
    },
    normalizedActiveSide() {
      return this.activeSide === 'right' ? 'right' : 'left';
    },
    activeAthlete() {
      return this.normalizedActiveSide === 'right' ? this.rightAthlete : this.leftAthlete;
    },
    leftAthleteDisplayName() {
      return this.formatAthleteName(this.leftAthlete);
    },
    rightAthleteDisplayName() {
      return this.formatAthleteName(this.rightAthlete);
    },
    activeAthleteDisplayName() {
      return this.formatAthleteName(this.activeAthlete);
    },
    leftCountryCode() {
      return String(this.leftAthlete?.countryCode || '').trim().toUpperCase() || '--';
    },
    rightCountryCode() {
      return String(this.rightAthlete?.countryCode || '').trim().toUpperCase() || '--';
    },
    activeCountryCode() {
      return this.normalizedActiveSide === 'right' ? this.rightCountryCode : this.leftCountryCode;
    },
    leftTimerDisplayParts() {
      return this.buildTimerDisplayParts(this.leftRemainingSeconds, this.leftDisplay);
    },
    rightTimerDisplayParts() {
      return this.buildTimerDisplayParts(this.rightRemainingSeconds, this.rightDisplay);
    },
    activeTimerDisplayParts() {
      return this.normalizedActiveSide === 'right' ? this.rightTimerDisplayParts : this.leftTimerDisplayParts;
    },
    inactiveTimerDisplayParts() {
      return this.normalizedActiveSide === 'right' ? this.leftTimerDisplayParts : this.rightTimerDisplayParts;
    },
    activeOvertime() {
      return this.normalizedActiveSide === 'right' ? this.rightOvertime : this.leftOvertime;
    },
    inactiveOvertime() {
      return this.normalizedActiveSide === 'right' ? this.leftOvertime : this.rightOvertime;
    },
    activeStateValue() {
      return this.normalizedActiveSide === 'right' ? this.rightStateValue : this.leftStateValue;
    },
    activeIsRecording() {
      return this.normalizedActiveSide === 'right' ? this.rightIsRecording : this.leftIsRecording;
    },
    activeStateLabel() {
      return this.normalizedActiveSide === 'right' ? this.rightStateLabel : this.leftStateLabel;
    },
    resolvedStatusIcon() {
      const state = String(this.activeStateValue || '').toLowerCase();
      if (state === 'paused') return 'pause';
      if (state === 'running') return 'play';
      return 'stop';
    },
    customPowerLabel() {
      return this.activeIsRecording ? 'ON' : 'OFF';
    },
    customPowerClass() {
      return this.activeIsRecording
        ? 'custom-power-pill--on'
        : 'custom-power-pill--off';
    },
    customSlotStyles() {
      return resolveCustomTimerSlotStyles('vs', this.normalizedTheme, this.viewportWidth, this.viewportHeight);
    },
    customStyleVars() {
      return resolveCustomTimerStyleVars('vs', this.normalizedTheme, this.viewportWidth, this.viewportHeight);
    },
    customLayoutProfile() {
      return resolveCustomTimerLayoutProfile(this.viewportWidth, this.viewportHeight);
    },
  },
  mounted() {
    this.updateViewportWidth();
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.updateViewportWidth, { passive: true });
    }
  },
  beforeUnmount() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.updateViewportWidth);
    }
  },
  methods: {
    updateViewportWidth() {
      if (typeof window === 'undefined') return;
      this.viewportWidth = window.innerWidth || 1920;
      this.viewportHeight = window.innerHeight || 1080;
    },
    getCustomSlotStyle(slotName) {
      return this.customSlotStyles?.[slotName] || null;
    },
    buildTimerDisplayParts(remainingSeconds, fallbackDisplay) {
      const numericValue = Number(remainingSeconds);
      if (!Number.isFinite(numericValue)) {
        return {
          main: fallbackDisplay || '00:00',
          fraction: '00',
        };
      }

      const absoluteValue = Math.max(0, Math.abs(numericValue));
      const wholeSeconds = Math.floor(absoluteValue);
      const minutes = Math.floor(wholeSeconds / 60).toString().padStart(2, '0');
      const seconds = (wholeSeconds % 60).toString().padStart(2, '0');
      const centiseconds = Math.min(99, Math.floor((absoluteValue - wholeSeconds) * 100 + 0.0001))
        .toString()
        .padStart(2, '0');

      return {
        main: `${minutes}:${seconds}`,
        fraction: centiseconds,
      };
    },
    formatAthleteName(athlete) {
      const firstName = String(athlete?.firstName || '').trim();
      const lastName = String(athlete?.lastName || '').trim();
      if (lastName || firstName) {
        return [firstName, lastName].filter(Boolean).join(' ').trim();
      }

      const fullName = String(athlete?.name || '').trim();
      if (!fullName) return '--';

      const parts = fullName.split(/\s+/).filter(Boolean);
      if (parts.length <= 1) return fullName;

      return parts.join(' ');
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Orbitron:wght@700;800;900&display=swap');

@font-face {
  font-family: 'TimerDigits';
  src: url('../../assets/fonts/LCDAT&TPhoneTimeDate.ttf') format('truetype');
  font-weight: 400;
  font-style: normal;
}

.timer-vs-display {
  width: 100vw;
  min-height: 100vh;
}

.vs-custom-layout {
  position: relative;
  width: 100vw;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.vs-custom-stage {
  position: relative;
  width: min(100vw, calc(100vh * 16 / 9), 100vw);
  max-width: 100vw;
  max-height: 100vh;
  aspect-ratio: 16 / 9;
  flex: 0 0 auto;
}

.vs-custom-mask {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.vs-custom-mask__image {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.vs-custom-overlay {
  position: absolute;
  inset: 0;
  z-index: 2;
}

.vs-custom-slot {
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
}

.vs-custom-slot__label {
  position: absolute;
  top: -12px;
  left: 12px;
  padding: 2px 8px;
  border-radius: 999px;
  background: rgba(59, 130, 246, 0.96);
  color: #eff6ff;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  display: none;
}

.vs-custom-overlay--debug .vs-custom-slot {
  outline: 2px dashed rgba(59, 130, 246, 0.95);
  background: rgba(15, 23, 42, 0.18);
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.18);
}

.vs-custom-overlay--debug .vs-custom-slot__label {
  display: inline-flex;
}

.vs-custom-layout-debug-chip {
  position: absolute;
  top: 12px;
  left: 12px;
  z-index: 3;
  display: inline-flex;
  align-items: center;
  padding: 6px 10px;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.9);
  color: #eff6ff;
  font-size: 0.76rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.vs-custom-name {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  color: #000000;
  text-align: left;
  font-size: var(--vs-name-font-size, clamp(3.3rem, 4.4vw, 4.5rem));
  font-weight: 800;
  font-family: 'Orbitron', sans-serif;
  text-transform: uppercase;
  line-height: 1;
  display: flex;
  align-items: baseline;
  box-sizing: border-box;
}

.vs-custom-name__text {
  min-width: 0;
  flex: 1 1 auto;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: clip;
}

.vs-custom-name__dot {
  flex: 0 0 auto;
  color: currentColor;
  line-height: 1;
}

.vs-custom-country {
  color: #ffffff;
  font-size: var(--vs-country-font-size, clamp(1.8rem, 3.2vw, 3.4rem));
  font-weight: 800;
  letter-spacing: 0.08em;
  line-height: 1;
  text-transform: uppercase;
}

.custom-status-icon {
  width: 100%;
  height: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--vs-status-gap, clamp(8px, 1vw, 14px));
  background: transparent;
}

.custom-status-icon__pause-bar {
  width: var(--vs-status-pause-width, clamp(10px, 0.9vw, 16px));
  height: var(--vs-status-pause-height, clamp(44px, 5.2vw, 72px));
  border-radius: 999px;
  background: #dc2626;
}

.custom-status-icon__play {
  width: 0;
  height: 0;
  border-top: var(--vs-status-play-top, clamp(22px, 2vw, 30px)) solid transparent;
  border-bottom: var(--vs-status-play-bottom, clamp(22px, 2vw, 30px)) solid transparent;
  border-left: var(--vs-status-play-left, clamp(34px, 3vw, 48px)) solid #16a34a;
  margin-left: var(--vs-status-play-offset, clamp(6px, 0.5vw, 10px));
}

.custom-status-icon__stop {
  width: var(--vs-status-stop-size, clamp(40px, 3.2vw, 58px));
  height: var(--vs-status-stop-size, clamp(40px, 3.2vw, 58px));
  background: #dc2626;
}

.custom-power-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  border-radius: var(--vs-power-radius, 18px);
  font-size: var(--vs-power-font-size, clamp(1.8rem, 3vw, 3rem));
  font-weight: 900;
  letter-spacing: 0.08em;
  color: #ffffff;
}

.custom-power-pill--on {
  color: #16a34a;
}

.custom-power-pill--off {
  color: #dc2626;
}

.vs-custom-timer {
  width: 100%;
  color: #111111;
  text-align: center;
  font-family: 'TimerDigits', sans-serif;
  font-size: var(--vs-main-timer-font-size, clamp(7rem, 22vw, 18rem));
  font-weight: 100;
  letter-spacing: 0.01em;
  line-height: 1;
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum' 1;
  white-space: nowrap;
  display: inline-flex;
  align-items: baseline;
  justify-content: center;
  column-gap: 0.12em;
}

.vs-custom-timer--secondary {
  font-size: var(--vs-secondary-timer-font-size, clamp(4.2rem, 8.3vw, 7.4rem));
}

.vs-custom-timer__main {
  display: block;
  line-height: 1;
}

.vs-custom-timer__fraction {
  display: block;
  font-size: 0.34em;
  line-height: 1;
  letter-spacing: 0.04em;
}

.vs-custom-timer--overtime {
  color: #dc2626;
}

.timer-vs-display--custom .vs-custom-timer.vs-custom-timer--overtime {
  color: #dc2626;
}

.timer-layout {
  width: 100vw;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.timer-layout--vs {
  padding-top: clamp(72px, 8vw, 106px);
  padding-bottom: clamp(72px, 8vw, 106px);
}

.athlete-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  width: 100vw;
  padding: clamp(8px, 1.4vw, 14px) clamp(10px, 2.4vw, 24px);
  border-top: 1px solid rgba(226, 232, 240, 0.22);
  border-bottom: 1px solid rgba(226, 232, 240, 0.22);
  background: transparent;
  box-sizing: border-box;
}

.athlete-bar--top {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 9;
  min-height: clamp(72px, 8vw, 106px);
}

.athlete-bar--bottom {
  position: fixed;
  bottom: 0;
  left: 0;
  z-index: 9;
  min-height: clamp(72px, 8vw, 106px);
}

.athlete-bar__info {
  display: flex;
  align-items: center;
  gap: clamp(8px, 1.4vw, 16px);
  min-width: 0;
  flex: 1;
}

.athlete-bar__info--reverse {
  justify-content: flex-end;
  text-align: right;
}

.flag {
  display: flex;
  align-items: center;
  justify-content: center;
  width: clamp(86px, 10vw, 150px);
  aspect-ratio: 16 / 9;
  flex: 0 0 auto;
}

.vs-body {
  flex: 1;
  width: 100%;
  display: flex;
  min-height: 0;
  align-items: center;
  justify-content: center;
}

.vs-timer {
  font-size: clamp(5rem, 15vw, 14rem);
  line-height: 1;
  font-weight: 900;
  letter-spacing: clamp(0.6px, 0.8vw, 2px);
  text-align: center;
  color: currentColor;
  width: 100%;
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum' 1;
}

.vs-timer--overtime {
  color: #ef4444;
}

.vs-divider {
  position: fixed;
  top: 50%;
  left: 0;
  width: 100vw;
  display: flex;
  align-items: center;
  justify-content: center;
  transform: translateY(-50%);
  z-index: 8;
  pointer-events: none;
}

.vs-divider__line {
  width: 100%;
  height: 1px;
  background: currentColor;
  opacity: 0.35;
}

.vs-divider__label {
  position: absolute;
  padding: 6px 14px;
  border: 1px solid currentColor;
  border-radius: 999px;
  background: inherit;
  font-weight: 900;
  letter-spacing: 1px;
  font-size: clamp(1rem, 1.8vw, 1.35rem);
}

.athlete-name {
  font-weight: 800;
  font-size: clamp(1.9rem, 3.6vw, 2.8rem);
  color: currentColor;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
}

.status-pill {
  padding: 10px 14px;
  border-radius: 12px;
  font-weight: 800;
  text-transform: uppercase;
  font-size: clamp(0.95rem, 2.2vw, 1.2rem);
  background: #1e293b;
  color: #f8fafc;
}

.status-pill--running { background: #16a34a; color: #f8fafc; }
.status-pill--paused { background: #d97706; color: #f8fafc; }
.status-pill--finished { background: #475569; color: #f8fafc; }
.status-pill--idle { background: #334155; color: #f8fafc; }
.status-pill--waiting { background: #2563eb; color: #f8fafc; }

.flag-img {
  width: 100% !important;
  height: 100% !important;
  object-fit: cover;
  border-radius: 8px;
}

:deep(.flag-svg),
:deep(flag) {
  width: 100% !important;
  height: 100% !important;
  display: block;
}

:deep(.flag-svg svg),
:deep(.flag-svg img),
:deep(flag svg),
:deep(flag img) {
  width: 100% !important;
  height: 100% !important;
  display: block;
  object-fit: cover;
}

.timer-vs-display--light {
  color: #0f172a;
}

.timer-vs-display--light .athlete-bar {
  border-top-color: rgba(15, 23, 42, 0.2);
  border-bottom-color: rgba(15, 23, 42, 0.2);
}

.timer-vs-display--light .vs-divider__line {
  opacity: 0.26;
}

.timer-vs-display--light .vs-divider__label {
  background: #ffffff;
}

.timer-vs-display--light .status-pill {
  color: #ffffff;
}

.timer-vs-display--light .vs-timer--overtime {
  color: #dc2626;
}

.timer-vs-display--custom-2 .vs-custom-layout {
  width: 100vw;
  min-height: 100vh;
}

.timer-vs-display--custom-2 .vs-custom-stage {
  width: 100vw;
  max-width: none;
  height: 100vh;
  max-height: none;
  aspect-ratio: auto;
}

.timer-vs-display--custom-2 .vs-custom-mask__image {
  width: 100vw;
  height: 100vh;
  max-width: none;
  object-fit: cover;
  object-position: center center;
}

.timer-vs-display--custom-2 .vs-custom-slot--athlete-name {
  overflow: hidden;
}

.timer-vs-display--custom-2 .vs-custom-name {
  max-width: 100%;
}

.timer-vs-display--custom-2 .vs-custom-name__text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.timer-vs-display--custom-2 .vs-custom-name__dot {
  display: none;
}

@media (max-width: 1024px) {
  .athlete-bar {
    width: 100vw;
  }

  .athlete-bar--top,
  .athlete-bar--bottom {
    min-height: clamp(68px, 9vw, 94px);
    padding-left: clamp(10px, 3vw, 16px);
    padding-right: clamp(10px, 3vw, 16px);
  }

  .vs-timer {
    font-size: clamp(4.4rem, 13vw, 10rem);
  }
}

@media (max-width: 768px) {
  .athlete-bar {
    width: 100vw;
    padding: 9px 12px;
  }

  .athlete-name {
    font-size: clamp(1.3rem, 4.4vw, 1.9rem);
  }

  .flag {
    width: clamp(62px, 16vw, 98px);
  }

  .status-pill {
    font-size: clamp(0.9rem, 3vw, 1.05rem);
    padding: 7px 10px;
  }

  .vs-timer {
    font-size: clamp(3.6rem, 14vw, 7.8rem);
  }

  .vs-divider__label {
    padding: 5px 10px;
  }
}

@media (max-width: 540px) {
  .athlete-name {
    font-size: clamp(1.15rem, 4.8vw, 1.6rem);
  }

  .athlete-bar {
    gap: 8px;
  }

  .status-pill {
    padding: 6px 8px;
    font-size: clamp(0.72rem, 2.8vw, 0.92rem);
  }

  .vs-timer {
    font-size: clamp(3rem, 15vw, 6.2rem);
  }
}
</style>